import { useEffect, useState } from 'react';
import logoIcon from '@/assets/loop-icon.png';

/**
 * The full-screen wait while the app works out who you are.
 *
 * It is the first thing anyone sees, and it is the one screen that has no navigation, no
 * header and nothing to click — so when the auth check stalls there is nothing to do but
 * stare at it. After half a minute it stops being a loading screen and says what to try,
 * because the causes are nearly always local: a dropped connection, or an extension
 * holding the page. Nothing here delays anything; the app renders the moment auth lands.
 */
const STUCK_AFTER_MS = 30_000;

export default function AppLoadingScreen({ label = 'Loading PatentLoop' }: { label?: string }) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setStuck(true), STUCK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden
                 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-6"
      role="status"
      aria-live="polite"
    >
      {/* Two soft lights behind the card. They give the flat gradient some depth without
          anything moving fast enough to notice on a screen you may sit in front of. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 top-[-10%] h-[28rem] w-[28rem] rounded-full
                   bg-blue-500/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-[-15%] h-[32rem] w-[32rem] rounded-full
                   bg-indigo-500/20 blur-3xl"
      />

      <div className="relative w-full max-w-md text-center">
        <img
          src={logoIcon}
          alt="PatentLoop"
          className="mx-auto h-9 w-auto opacity-95"
        />

        <p className="mt-6 text-sm font-medium tracking-wide text-slate-200">{label}</p>

        {/* An indeterminate bar rather than a spinner: it reads as a system starting up,
            and it does not pretend to know how far along it is. */}
        <div className="mx-auto mt-4 h-0.5 w-48 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-1/3 animate-[loading-sweep_1.4s_ease-in-out_infinite] rounded-full bg-blue-300/80" />
        </div>

        {stuck && (
          <div className="mt-8 rounded-lg border border-white/10 bg-white/5 p-4 text-left backdrop-blur-sm">
            <p className="text-sm font-medium text-white">This is taking longer than it should</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
              Please try reloading the page. If the problem persists, disable your browser
              extensions — an ad blocker or password manager can hold requests from this
              page — or contact{' '}
              <a
                href="mailto:info@patentloop.com"
                className="font-medium text-blue-300 underline underline-offset-2 hover:text-blue-200"
              >
                info@patentloop.com
              </a>
              .
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 rounded-md border border-white/20 px-3 py-1.5 text-sm font-medium
                         text-white transition hover:bg-white/10"
            >
              Reload the page
            </button>
          </div>
        )}
      </div>

      {/* Inline rather than in the Tailwind config: it belongs to this screen, and this
          screen has to render before anything else is set up. */}
      <style>{`
        @keyframes loading-sweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}
